import React from 'react';
import SafeIcon from '../../common/SafeIcon';

const BuiltBeyondPage = () => {
  return (
    <section id="systems" className="py-24 relative overflow-hidden">
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-[600px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="w-full max-w-[1440px] 2xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-12 items-start">
          <div className="lg:col-span-5 xl:col-span-5 self-start">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-5">
              <SafeIcon name="Settings" className="w-4 h-4" />
              Systems Behind the Sites
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight max-w-[620px]">
              What should a business website do beyond looking professional?
            </h2>
            <div className="space-y-5 text-base sm:text-lg text-slate-200/90 leading-relaxed max-w-[620px]">
              <p>
                Most businesses do not lose leads because their website looks bad. They lose leads because the enquiry path is broken, unclear, slow, unprotected, or difficult to follow up.
              </p>
              <p>
                BPA Solutions builds website systems that help capture the right information, protect the enquiry path, confirm the customer, notify the business, record the lead and support structured follow-up.
              </p>
              <p>
                We can also support a more professional business presence with branded email addresses, structured service pages, location coverage and answer-ready website content.
              </p>
              <p className="font-medium text-white border-l-4 border-blue-500 pl-4 py-1">
                The result is not just a website. It is a front-end business system designed to reduce missed opportunities and support trust, speed and conversion.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 xl:col-span-7 self-start">
            <div className="rounded-[2rem] border border-white/10 bg-[#14141d]/70 backdrop-blur-xl p-5 sm:p-6 lg:p-7 shadow-[0_24px_90px_rgba(0,0,0,0.35)]">
              <h3 className="text-xl font-bold text-white mb-2">Seven lead leaks we help fix</h3>
              <p className="text-sm text-slate-400 mb-5">
                Small gaps in the enquiry journey can cost real opportunities. These are the systems we build into the website experience.
              </p>
              <div className="space-y-3">
                {[
                  {
                    icon: "Shield",
                    title: "Spam-Protected Lead Capture",
                    description: "The enquiry system should capture real customer details while reducing bot, spam and low-quality submissions."
                  },
                  {
                    icon: "FileText",
                    title: "Enquiry Forms That Capture the Right Details",
                    description: "Structured forms help customers submit useful information instead of incomplete or unclear enquiries."
                  },
                  {
                    icon: "Mail",
                    title: "Customer Email & SMS Confirmations",
                    description: "Customers receive an immediate confirmation, know they have been heard and understand the next step."
                  },
                  {
                    icon: "Bell",
                    title: "Business SMS & Email Lead Alerts",
                    description: "The business is notified quickly when a new enquiry arrives, reducing missed callbacks and slow responses."
                  },
                  {
                    icon: "Calendar",
                    title: "Booking & Callback Pathways",
                    description: "Visitors can nominate when they want to be contacted, making follow-up easier and more organised."
                  },
                  {
                    icon: "Database",
                    title: "Lead Records & Follow-Up Tracking",
                    description: "Enquiries are recorded so they do not disappear into inboxes, phone notes or forgotten messages."
                  },
                  {
                    icon: "Globe",
                    title: "Professional Email, SEO, GEO & Answer-Ready Structure",
                    description: "Professional email addresses, service sections, location coverage and question-based content help strengthen trust and search intent."
                  }
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex gap-4 items-start rounded-2xl border border-white/10 bg-white/[0.045] p-4 hover:border-blue-400/25 hover:bg-white/[0.065] transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-400/15 flex items-center justify-center text-blue-300 shrink-0">
                      <SafeIcon name={item.icon} className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm sm:text-base font-semibold text-slate-100 block mb-1">
                        {item.title}
                      </span>
                      <span className="text-sm leading-relaxed text-slate-400">
                        {item.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuiltBeyondPage;